/* ============================================================
   MG Board v2 — 9 도메인 카드 + 카테고리 매핑
   v6.1 portal HTML 충실 이식 (의장님 단일 소스)
   ============================================================ */

export type Status = 'live' | 'dev' | 'plan';

export interface DomainCard {
  code: string;
  num: string;
  icon: string;
  label: string;
  desc: string;
  meta: string;
  status: Status;
  isHub: boolean;
}

export const DOMAIN_CARDS: DomainCard[] = [
  { code: 'SCHEDULE', num: '01', icon: 'CalendarClock', label: 'MG 일정관리',
    desc: '일정·태스크 통합 허브 (테이블·간트·칸반·캘린더 4뷰)',
    meta: 'v_tasks_all 13 sources', status: 'dev', isHub: false },
  { code: 'OPS', num: '02', icon: 'Briefcase', label: 'MG 운영',
    desc: '회사·법인·브랜드·창업·세무·상표',
    meta: 'mg_corp + mg_brand', status: 'plan', isHub: false },
  { code: 'FIELD', num: '03', icon: 'HardHat', label: 'MG 현장',
    desc: '공사 프로젝트별 통합 관리 (고객·협력사·정산·자재·안전·법무)',
    meta: 'mg_const + mg_specs + mg_arch', status: 'plan', isHub: false },
  { code: 'FINANCE', num: '04', icon: 'Coins', label: 'MG 재무',
    desc: '자금·매출·비용·세무·현금흐름',
    meta: '신설 예정', status: 'plan', isHub: false },
  { code: 'DEVLOG', num: '05', icon: 'Code2', label: 'MG 시스템',
    desc: '시스템 개발 프로젝트·기술부채·스키마 거버넌스',
    meta: 'mg_devlog + mg_system + mg_security', status: 'dev', isHub: false },
  { code: 'LEARN', num: '06', icon: 'BookOpen', label: '지식·학습',
    desc: '영어·독서(마샘)·지식 증류·MG Wiki',
    meta: 'eng + books + knowledge', status: 'dev', isHub: false },
  { code: 'LIFE', num: '07', icon: 'Home', label: '삶·가족',
    desc: '가족 의료·돌봄·보험·라이프 입력',
    meta: 'mg_life + mg_family', status: 'dev', isHub: false },
  { code: 'ETHOS', num: '08', icon: 'GraduationCap', label: '에트OS',
    desc: '에트 통합 학습 대시보드 (내신·학습·입시·신앙·진로)',
    meta: 'ethe', status: 'live', isHub: false },
  { code: 'FAITH', num: '09', icon: 'HeartHandshake', label: '사역·신앙',
    desc: 'Q&A·말씀·기도·돌샘순·위더스·인비저블핸즈',
    meta: 'faith + ih', status: 'dev', isHub: false }
];

export const STATUS_LABEL: Record<Status, string> = {
  live: '운영중',
  dev: '개발 진행중',
  plan: '기획 단계'
};

export const DOMAIN_PANELS: Record<string, string[]> = {
  SCHEDULE: ['테이블 뷰', '간트 차트', '칸반 보드', '캘린더 뷰', 'Daily Handoff', 'Road Ahead', 'GCal 동기화'],
  OPS: ['사업체 마스터', '법인 마일스톤', '의사결정 기록', '세무 신고', '창업 비용', '상표 출원', '브랜드 정체성'],
  FIELD: ['현장 목록', '고객 관리', '협력사 관리', '공정 진행 (9 phase)', '자재 관리', '안전 관리', '법무·세무', '정산', '도면·설계자료'],
  FINANCE: ['매출 (TBD)', '비용 (TBD)', '현금흐름 (TBD)', '세무 신고 연동'],
  DEVLOG: ['프로젝트 현황', '기술 부채', '스키마 변경 이력', 'Rule·SOP·Memory', '보안 조치 이력'],
  LEARN: ['영어 심화', '독서 (마샘 95권)', '지식 증류 (knowledge)', 'MG Wiki'],
  LIFE: ['가족 구성원', '진단·복약·내원', '돌봄 노트', '보험 계획', '라이프 아이템'],
  ETHOS: ['내신 학습 대시보드', '입시 진로 (한국화과)', '건강·신앙·일과', '수행평가·시험'],
  FAITH: ['신앙 Q&A 60건', '말씀 노트 80건', '기도제목·로그', '돌샘순 (위더스)', '선교 단체·IH']
};

/* ============================================================
   카탈로그 (사이드바) — v2.0 메인 9카드 1:1 정합 재편
   2026-05-12: 7카테고리 → 9카테고리 + 인프라 1개 = 10개

   v2.3 (2026-05-12 정정): SubGroup 디자인 schema-card 그대로 정합.
   본 commit으로 v2.2 portal-card 패턴 시도 revert.
   의장님 발화 "모든 디자인 유지" 본질 정합 영역.

   v2.4 (2026-05-12 갱신): sub_groups 4건 label을 영문 Track 패턴 격상.
   FieldTrack desc에서 "SAWI 등 " 텍스트만 삭제 (괄호 본문 보존).
   ============================================================ */

/* SubGroup은 schema-card와 동일 시각 구조로 렌더링됨.
   schema-name: target (영문, JetBrains Mono)
   schema-label: label (한글, Cormorant Garamond)
   schema-desc: desc (본질 설명) */
export interface SubGroup {
  key: string;          // 분류 식별자 (e.g., 'system_dev')
  label: string;        // 한글 라벨 — schema-label 위치 (e.g., '시스템 개발 일정')
  target: string;       // 진입 대상 schema 또는 view — schema-name 위치
  target_type: 'schema' | 'view';
  desc: string;         // 본질 설명 — schema-desc 위치
}

export interface CategoryDef {
  name: string;
  order: number;
  icon: string;
  desc: string;
  schemas: string[];
  /* v2.1: 본 카테고리의 sub_groups 분류 표시.
     CategoryView 본문에서 schemas 그리드 대신 sub_groups를 schema-card 패턴으로 렌더.
     의장님 4분류 본질 정합 영역 (mg_memory #119). */
  sub_groups?: SubGroup[];
}

export const CATEGORY_DEF: CategoryDef[] = [
  { name: '일정·프로젝트', order: 1, icon: 'CalendarClock',
    desc: '태스크·프로젝트·일정 (메인 01번 카드)',
    schemas: ['mg_devlog', 'mg_ground'],
    sub_groups: [
      { key: 'system_dev',
        label: 'DevTrack',
        target: 'mg_devlog',
        target_type: 'schema',
        desc: 'MG-SYSTEM 자체 개발 task (BOARD·ETHOS·MG-Spec 등)' },
      { key: 'construction',
        label: 'FieldTrack',
        target: 'mg_const',
        target_type: 'schema',
        desc: '공사 현장 task (시공 프로젝트)' },
      { key: 'corp_ops',
        label: 'OpsTrack',
        target: 'mg_ground',
        target_type: 'schema',
        desc: '회사 차원 운영 (MG Field·MosaekGround·MoFly·Monate·브랜드)' },
      { key: 'personal_life',
        label: 'LifeTrack',
        target: 'v_personal_tasks',
        target_type: 'view',
        desc: '사적 영역 통합 (mg_life + mg_family + faith)' }
    ]
  },
  { name: 'MG 운영', order: 2, icon: 'Briefcase',
    desc: '회사·법인·브랜드·창업·세무·상표 (메인 02번 카드)',
    schemas: ['mg_corp', 'mg_brand'] },
  { name: 'MG 현장', order: 3, icon: 'HardHat',
    desc: '공사 프로젝트별 통합 관리 (메인 03번 카드)',
    schemas: ['mg_const', 'mg_specs', 'mg_arch'] },
  { name: 'MG 재무', order: 4, icon: 'Coins',
    desc: '자금·매출·비용·세무·현금흐름 (메인 04번 카드, 신설 예정)',
    schemas: [] },
  { name: 'MG 시스템', order: 5, icon: 'Code2',
    desc: '시스템 거버넌스·보안·인텔 (메인 05번 카드)',
    schemas: ['mg_system', 'mg_security', 'mg_intel'] },
  { name: '지식·학습', order: 6, icon: 'BookOpen',
    desc: '영어·독서·지식 증류 (메인 06번 카드)',
    schemas: ['eng', 'books', 'knowledge'] },
  { name: '삶·가족', order: 7, icon: 'Home',
    desc: '가족 돌봄·라이프 입력 (메인 07번 카드)',
    schemas: ['mg_family', 'mg_life'] },
  { name: '에트OS', order: 8, icon: 'GraduationCap',
    desc: '에트 통합 학습 대시보드 (메인 08번 카드)',
    schemas: ['ethe'] },
  { name: '사역·신앙', order: 9, icon: 'HeartHandshake',
    desc: 'Q&A·말씀·기도·IH (메인 09번 카드)',
    schemas: ['faith', 'ih'] },
  { name: '공용·인프라', order: 10, icon: 'Layers',
    desc: '공용 뷰·벡터 DB (메인 미노출)',
    schemas: ['public', 'vectors'] }
];

/* SCHEMA_LABELS — schema-card 좌측 상단 영문 schema명에 대응되는 한글 라벨.
   sub_groups 진입 시에도 본 매핑 활용 (target이 v_xxx인 경우 fallback 처리). */
export const SCHEMA_LABELS: Record<string, string> = {
  mg_system: '시스템 메타', mg_security: '보안',
  mg_devlog: '시스템 개발', mg_ground: '전사 일정·태스크',
  mg_const: '공사 CM', mg_life: '삶',
  mg_corp: '법인', mg_brand: '브랜드', mg_arch: '설계 자료',
  mg_specs: '자재 라이브러리', mg_intel: '인텔리전스',
  mg_family: '가족', faith: '신앙 기록', ih: '인비저블핸즈',
  ethe: '에트OS', eng: '영어 심화', books: '독서 (마샘)', knowledge: '지식 증류',
  public: '공용 (RLS 통과)', vectors: '벡터 DB',
  /* v2.3: 통합 뷰 라벨 추가 (sub_groups target_type='view'용) */
  v_tasks_all: '통합 일정 허브',
  v_personal_tasks: '개인·가족·사역 통합'
};

export const SCHEMA_DESC: Record<string, string> = {
  mg_system: '운영 규칙·SOP·장기기억·진행이력',
  mg_security: '감사·키 대장·관리자 PIN·정책',
  mg_devlog: '개발 프로젝트·태스크·이슈·노트',
  mg_ground: '전사 프로젝트·태스크·박스·캘린더',
  mg_const: '공사 프로젝트·건축주·협력사·태스크',
  mg_life: '라이프 프로젝트·아이템·태스크',
  mg_corp: '법인·문서·마일스톤·세무·상표',
  mg_brand: '브랜드·아이덴티티·폰트 라이브러리',
  mg_arch: '맥락 문서·설계 표준·BIM·CAD·자동화',
  mg_specs: '자재 마스터·벤더 가격·시세 이력',
  mg_intel: '인텔 항목·이벤트 추적·리스크',
  mg_family: '가족·진단·복약·내원·돌봄·보험',
  faith: 'Q&A·말씀 노트·기도·선교 단체',
  ih: 'IH 원칙·세계 이슈·파트너 단체·기도',
  ethe: '교과서·지문·단어·퀴즈·시험·성적·일과',
  eng: '문장 분석·독해·어휘·문법·퀴즈',
  books: '도서·하이라이트·노트·리뷰·콘텐츠',
  knowledge: '토픽·개념·How-To·참조',
  public: 'ethe 미러 뷰 (RLS 통과)',
  vectors: '벡터 임베딩 문서',
  /* v2.3: 통합 뷰 desc 추가 */
  v_tasks_all: '모든 task 단일 진입점 (13 sources UNION)',
  v_personal_tasks: '사적 영역 통합 (mg_life + mg_family + faith)'
};

export const DEFAULT_OPEN_CATS = new Set(['일정·프로젝트', 'MG 시스템']);
